/**
 * Central feature flag configuration for all platforms and regions.
 *
 * This file provides a unified way to configure:
 * - Feature flags (e.g., helpTab)
 * - Specimen type availability (e.g., prostateBiopsy, breastBiopsy)
 * - Any other platform/region-specific feature toggles
 *
 * All features are configured in a single matrix for easy management.
 */
import { getPlatform, getRegion } from 'src/config/environment';
import { DistributionPlatform, Region } from 'src/types/env';

/**
 * All available feature flags in the system.
 * Includes both UI features and order type availability.
 */
export enum FeatureFlag {
  // UI Features
  OrganizationSelector = 'organizationSelector',
  HelpTab = 'helpTab',
}

/**
 * Feature configuration type.
 * Currently just tracks enabled/disabled, but can be extended with
 * additional metadata (rollout percentage, user segments, etc.)
 */
export type FeatureConfig = {
  enabled: boolean;
};

/**
 * Central feature matrix: Platform -> Region -> Feature -> Config
 * When a region is defined, all feature flags must be explicitly set.
 */
type FeatureMatrix = Record<
  DistributionPlatform,
  Partial<Record<Region, Record<FeatureFlag, FeatureConfig>>>
>;

const FEATURE_MATRIX: FeatureMatrix = {
  [DistributionPlatform.Ldt]: {
    [Region.US]: {
      [FeatureFlag.OrganizationSelector]: { enabled: true },
      [FeatureFlag.HelpTab]: { enabled: false },
    },
  },
  [DistributionPlatform.Samd]: {
    [Region.US]: {
      [FeatureFlag.OrganizationSelector]: { enabled: false },
      [FeatureFlag.HelpTab]: { enabled: true },
    },
    [Region.AU]: {
      [FeatureFlag.OrganizationSelector]: { enabled: false },
      [FeatureFlag.HelpTab]: { enabled: true },
    },
    [Region.UK]: {
      [FeatureFlag.OrganizationSelector]: { enabled: false },
      [FeatureFlag.HelpTab]: { enabled: true },
    },
    [Region.EU]: {
      [FeatureFlag.OrganizationSelector]: { enabled: false },
      [FeatureFlag.HelpTab]: { enabled: true },
    },
    [Region.SG]: {
      [FeatureFlag.OrganizationSelector]: { enabled: false },
      [FeatureFlag.HelpTab]: { enabled: true },
    },
  },
  [DistributionPlatform.Clin]: {
    [Region.AU]: {
      [FeatureFlag.OrganizationSelector]: { enabled: false },
      [FeatureFlag.HelpTab]: { enabled: false },
    },
  },
};

/**
 * Dependency injection interface for testability.
 * Allows tests to mock platform/region detection.
 */
export interface FeatureContext {
  getPlatform: () => DistributionPlatform;
  getRegion: () => Region;
}

/**
 * Default context using actual environment functions.
 */
function getDefaultContext(): FeatureContext {
  return {
    getPlatform,
    getRegion,
  };
}

/**
 * Check if a feature is enabled for the current platform/region.
 *
 * @param feature - The feature flag to check
 * @param context - Optional context for testing (uses default if not provided)
 * @returns true if the feature is enabled, false otherwise
 */
export function isFeatureEnabled(
  feature: FeatureFlag,
  context?: FeatureContext,
): boolean {
  const ctx = context ?? getDefaultContext();
  const platform = ctx.getPlatform();
  const region = ctx.getRegion();

  return FEATURE_MATRIX[platform]?.[region]?.[feature]?.enabled ?? false;
}

/**
 * Get the configuration for a specific feature.
 *
 * @param feature - The feature flag to get config for
 * @param context - Optional context for testing (uses default if not provided)
 * @returns The feature configuration or undefined if not configured
 */
export function getFeatureConfig(
  feature: FeatureFlag,
  context?: FeatureContext,
): FeatureConfig | undefined {
  const ctx = context ?? getDefaultContext();
  const platform = ctx.getPlatform();
  const region = ctx.getRegion();

  return FEATURE_MATRIX[platform]?.[region]?.[feature];
}
